import { notification } from 'antd';

import { appManager, AppState } from '../manager';
import { arrayToIndexedObject } from 'services/indexedObject';
import type { Session } from '../sessions/types';
import type {
  Blueprint,
  CreateBlueprintPayload,
  PromoteToBlueprintPayload,
  AddBlueprintVersionPayload,
  IntroduceDifficultyPayload,
  PublishBlueprintVersionPayload,
  SetVariantVisibilityPayload,
  LaunchBlueprintPayload,
  LaunchBlueprintResult,
  ValidateVariantResult,
} from './types';

// Session Blueprints redux slice — mirrors src/state/app/presets/actions.ts.
// Every list/retrieve/launch is trainer|admin; every write is admin-only
// (enforced server-side). Items are indexed by family id in state.blueprints.

// THE AUDIT HALF OF A LAUNCH CARD, AND HOW IT SURVIVES A DELIVERY THAT OMITS IT.
//
// A card has two halves. Everyone the catalogue admits — trainers and client
// observers included — reads the counts, the tickers, `launchable` and the
// neutral `launch_refusal_note`. Only an administrator reads these two: the
// answer key's quality gate, and the refusal sentence that quotes graded
// headlines, names synthetic bonds and states the pass-bar arithmetic. The
// audience table lives on SessionBlueprintSerializer (pm/blueprints/serializers.py).
const THE_AUDIT_HALF = ['answer_key_gate', 'launch_refusal'] as const;

type CardCarrier = { launch_summary?: Record<string, unknown> };

// Two deliveries carry a family WITHOUT that half, and both replace the stored
// one outright:
//
//   the catalog broadcast — klass socket (trainers) and admin socket (admins)
//   over one shared group. It reaches every open dashboard at once and cannot
//   check who is listening, so the evidence stays on GET /api/blueprints/
//   (pm/blueprints/broadcast.py). It fires on all six admin writes below, and
//   lands right after the reply to the button that caused it;
//
//   any reply from an admin write that forgets to name its reader. The backend
//   names it on all six (pm/blueprints/views.py, `self.get_serializer`); this
//   is the second lock, so a delivery that omits the half cannot erase one.
//
// ABSENT and `null` are deliberately different on the wire: absent means "this
// delivery does not carry it", `null` means "there is no answer key at all".
// Only the first is carried over — and only from what THIS client already
// holds, so a trainer's client, which never held the half, is never given one.
export const withKnownAuditHalf = (
  incoming: Blueprint,
  known?: Blueprint,
): Blueprint => {
  if (!known) return incoming;
  // Shared catalogue broadcasts omit admin-only tandem hints as well. Keep
  // only hints this client already received; a fresh HTTP hint replaces them.
  const tandem = new Map(
    (known.versions || []).flatMap(version =>
      version.variants.map(
        variant => [variant.id, variant.tandem_eligibility] as const,
      ),
    ),
  );
  const held = new Map<number, Record<string, unknown>>();
  known.versions?.forEach(version =>
    version.variants?.forEach(variant => {
      const card = (variant as unknown as CardCarrier).launch_summary;
      if (!card) return;
      const kept: Record<string, unknown> = {};
      THE_AUDIT_HALF.forEach(field => {
        if (field in card) kept[field] = card[field];
      });
      if (Object.keys(kept).length) held.set(variant.id, kept);
    }),
  );
  incoming.versions?.forEach(version =>
    version.variants?.forEach(variant => {
      if (!('tandem_eligibility' in variant) && tandem.get(variant.id)) {
        variant.tandem_eligibility = tandem.get(variant.id);
      }
      const card = (variant as unknown as CardCarrier).launch_summary;
      const kept = held.get(variant.id);
      if (!card || !kept) return;
      THE_AUDIT_HALF.forEach(field => {
        if (!(field in card) && field in kept) card[field] = kept[field];
      });
    }),
  );
  return incoming;
};

// The ONE way a whole family reaches redux from a write or a broadcast.
const storeFamily = (state: AppState, result: Blueprint) => {
  state.blueprints.items[result.id] = withKnownAuditHalf(
    result,
    state.blueprints.items[result.id],
  );
};

export const listBlueprints = appManager.createApi<
  { summary_scope?: 'none' | 'latest' },
  Blueprint[],
  AppState
>('LIST_BLUEPRINTS', {
  method: 'GET',
  path: '/blueprints',
  startReducer(state) {
    state.blueprints.waiting = true;
    state.blueprints.error = undefined;
  },
  successReducer(state, result) {
    state.blueprints.items = arrayToIndexedObject(result);
    state.blueprints.waiting = false;
  },
  failReducer(state) {
    state.blueprints.waiting = false;
    state.blueprints.error = 'Scenarios could not be loaded.';
  },
});

// A detail view must not resolve the assessment keys of the entire catalogue.
export const fetchBlueprint = appManager.createApi<
  {
    id: number;
    summary_scope?: 'none' | 'latest';
    summary_variant_id?: number;
  },
  Blueprint,
  AppState
>('FETCH_BLUEPRINT', {
  method: 'GET',
  path: '/blueprints/:id',
  startReducer(state) {
    state.blueprints.waiting = true;
    state.blueprints.error = undefined;
  },
  successReducer(state, result) {
    storeFamily(state, result);
    state.blueprints.waiting = false;
  },
  failReducer(state) {
    state.blueprints.waiting = false;
    state.blueprints.error = 'The blueprint could not be loaded.';
  },
});

export const createBlueprint = appManager.createApi<
  CreateBlueprintPayload,
  Blueprint,
  AppState
>('CREATE_BLUEPRINT', {
  method: 'POST',
  path: '/blueprints',
  startReducer(state) {
    state.blueprints.waiting = true;
  },
  successReducer(state, result) {
    storeFamily(state, result);
    state.blueprints.waiting = false;
  },
  failReducer(state, error) {
    state.blueprints.waiting = false;
    notification.error({ message: 'Create blueprint failed' });
  },
});

export const promoteToBlueprint = appManager.createApi<
  PromoteToBlueprintPayload,
  Blueprint,
  AppState
>('PROMOTE_TO_BLUEPRINT', {
  method: 'POST',
  path: '/blueprints/from_session',
  startReducer(state) {
    state.blueprints.waiting = true;
  },
  successReducer(state, result) {
    storeFamily(state, result);
    state.blueprints.waiting = false;
    notification.success({
      message: 'Saved as blueprint',
      description: result.name,
    });
  },
  failReducer(state, error) {
    state.blueprints.waiting = false;
    notification.error({ message: 'Save as blueprint failed' });
  },
});

export const addBlueprintVersion = appManager.createApi<
  AddBlueprintVersionPayload,
  Blueprint,
  AppState
>('ADD_BLUEPRINT_VERSION', {
  method: 'POST',
  path: '/blueprints/:id/add_version',
  startReducer(state) {
    state.blueprints.waiting = true;
  },
  successReducer(state, result) {
    storeFamily(state, result);
    state.blueprints.waiting = false;
  },
  failReducer(state, error) {
    state.blueprints.waiting = false;
    notification.error({ message: 'Add version failed' });
  },
});

export const introduceDifficulty = appManager.createApi<
  IntroduceDifficultyPayload,
  Blueprint,
  AppState
>('INTRODUCE_DIFFICULTY', {
  method: 'POST',
  path: '/blueprints/:id/introduce_difficulty',
  startReducer(state) {
    state.blueprints.waiting = true;
  },
  successReducer(state, result) {
    storeFamily(state, result);
    state.blueprints.waiting = false;
  },
  failReducer(state, error) {
    state.blueprints.waiting = false;
    notification.error({ message: 'Introduce difficulty failed' });
  },
});

export const publishBlueprintVersion = appManager.createApi<
  PublishBlueprintVersionPayload,
  Blueprint,
  AppState
>('PUBLISH_BLUEPRINT_VERSION', {
  method: 'POST',
  path: '/blueprints/:id/publish_version',
  startReducer(state) {
    state.blueprints.waiting = true;
  },
  successReducer(state, result) {
    storeFamily(state, result);
    state.blueprints.waiting = false;
    notification.success({ message: 'Version published' });
  },
  failReducer(state, error) {
    state.blueprints.waiting = false;
    notification.error({ message: 'Publish version failed' });
  },
});

export const setVariantVisibility = appManager.createApi<
  SetVariantVisibilityPayload,
  Blueprint,
  AppState
>('SET_VARIANT_VISIBILITY', {
  method: 'POST',
  path: '/blueprints/:id/set_visibility',
  successReducer(state, result) {
    storeFamily(state, result);
  },
  failReducer(state, error) {
    notification.error({ message: 'Update visibility failed' });
  },
});

export const launchBlueprint = appManager.createApi<
  LaunchBlueprintPayload,
  LaunchBlueprintResult,
  AppState
>('LAUNCH_BLUEPRINT', {
  method: 'POST',
  path: '/blueprints/:id/launch',
  startReducer(state) {
    state.blueprints.waiting = true;
  },
  successReducer(state, result) {
    state.blueprints.waiting = false;
    // The new session itself arrives on the klass socket (like createSession).
    if (result.warnings?.length) {
      notification.warning({
        message: 'Launched with warnings',
        description: result.warnings.join('\n'),
        duration: 0,
      });
    } else {
      notification.success({ message: 'Session launched' });
    }
  },
  failReducer(state, error) {
    state.blueprints.waiting = false;
    notification.error({ message: 'Launch failed' });
  },
});

export const validateVariant = appManager.createApi<
  { id: number; variant_id: number },
  ValidateVariantResult,
  AppState
>('VALIDATE_VARIANT', {
  method: 'GET',
  path: '/blueprints/:id/validate_variant',
  successReducer(state, result) {
    notification.info({
      message: 'Variant validated',
      description: result.recompute_queued
        ? 'Benchmark coverage recompute queued.'
        : 'Coverage is up to date.',
    });
  },
  failReducer(state, error) {
    notification.error({ message: 'Validate variant failed' });
  },
});

// Edit a blueprint template's historical window post-create. Hits the stock
// session PATCH (which is in BLUEPRINT_LOCKED_WRITE_ACTIONS), so the backend
// rejects it once the version is locked. Updates the session in redux so the
// authoring view reflects the new dates.
export const updateBlueprintDates = appManager.createApi<
  { id: number; start_date: string; end_date: string },
  Session,
  AppState
>('UPDATE_BLUEPRINT_DATES', {
  method: 'PATCH',
  path: '/sessions/:id',
  startReducer(state) {
    state.blueprints.waiting = true;
  },
  successReducer(state, result) {
    state.blueprints.waiting = false;
    if (result?.id) {
      state.sessions.items[result.id] = {
        ...state.sessions.items[result.id],
        ...result,
      };
    }
    notification.success({ message: 'Historical period updated' });
  },
  failReducer(state) {
    state.blueprints.waiting = false;
    notification.error({ message: 'Update historical period failed' });
  },
});

// Blueprint-only mode toggle -- the ONLY write path for time_advancement_type. Flipping to
// predetermined is validated server-side (config violations are rejected with the full list;
// the schedule may still be empty at flip time -- completeness is a launch-time check).
export const updateTimeAdvancementType = appManager.createApi<
  {
    id: number;
    time_advancement_type: 'fixed_intervals' | 'predetermined_intervals';
  },
  {
    session_id: number;
    time_advancement_type: 'fixed_intervals' | 'predetermined_intervals';
  },
  AppState
>('UPDATE_TIME_ADVANCEMENT_TYPE', {
  method: 'POST',
  path: '/sessions/:id/update_time_advancement_type',
  startReducer(state) {
    state.blueprints.waiting = true;
  },
  successReducer(state, result) {
    state.blueprints.waiting = false;
    const session = state.sessions.items[result.session_id];
    if (session) {
      session.time_advancement_type = result.time_advancement_type;
    }
    notification.success({ message: 'Time advancement mode updated' });
  },
  failReducer(state) {
    state.blueprints.waiting = false;
    notification.error({
      message: 'Update time advancement mode failed',
      description:
        'Flipping to predetermined requires a compliant blueprint (no client money, ' +
        'per-sector alpha, banned assets, risk-weighted approach, leverage, or banned benchmarks).',
    });
  },
});

// Full-replace editor for the predetermined advancement schedule. Server sorts and validates
// (unique business days STRICTLY inside the trading window) and re-queues benchmark coverage.
// start/end are NEVER touched -- the trading window owns them; authored dates are the
// in-between landings.
export const updateScheduleDates = appManager.createApi<
  { id: number; dates: string[] },
  {
    session_id: number;
    dates: string[];
  },
  AppState
>('UPDATE_SCHEDULE_DATES', {
  method: 'POST',
  path: '/sessions/:id/update_schedule_dates',
  startReducer(state) {
    state.blueprints.waiting = true;
  },
  successReducer(state, result) {
    state.blueprints.waiting = false;
    const session = state.sessions.items[result.session_id];
    if (session) {
      session.scenario_schedule_dates = result.dates.map((date, index) => ({
        index,
        date,
      }));
    }
    notification.success({ message: 'Advancement schedule saved' });
  },
  failReducer(state) {
    state.blueprints.waiting = false;
    notification.error({
      message: 'Save advancement schedule failed',
      description:
        'Dates must be unique business days strictly inside the trading window.',
    });
  },
});

// Edit a blueprint template's news-importance levels. Same stock-PATCH mechanism
// as updateBlueprintDates (lock-guarded server-side).
export const updateBlueprintNewsImportances = appManager.createApi<
  { id: number; news_importances: number[] },
  Session,
  AppState
>('UPDATE_BLUEPRINT_NEWS_IMPORTANCES', {
  method: 'PATCH',
  path: '/sessions/:id',
  startReducer(state) {
    state.blueprints.waiting = true;
  },
  successReducer(state, result) {
    state.blueprints.waiting = false;
    if (result?.id) {
      state.sessions.items[result.id] = {
        ...state.sessions.items[result.id],
        ...result,
      };
    }
    notification.success({ message: 'News importance updated' });
  },
  failReducer(state) {
    state.blueprints.waiting = false;
    notification.error({ message: 'Update news importance failed' });
  },
});

export const deleteBlueprint = appManager.createApi<
  { id: number },
  unknown,
  AppState
>('DELETE_BLUEPRINT', {
  method: 'DELETE',
  path: '/blueprints/:id',
  successReducer(state, result, payload) {
    delete state.blueprints.items[payload.id];
  },
  failReducer(state, error) {
    notification.error({ message: 'Delete blueprint failed' });
  },
});

// Live blueprint-catalog updates, delivered on both the klass socket (trainers) and the admin socket
// (admins) via the shared broadcast group. Keeps the trainer launch picker + admin grid fresh in real
// time — e.g. hiding a variant removes it from an already-open picker without a page refresh. It
// carries the trainer's card only; `storeFamily` is what keeps this client's audit half (see above).
appManager.createSocketListener<Blueprint, AppState>(
  'blueprint_updated',
  (state, result) => {
    if (result?.id) {
      storeFamily(state, result);
    }
  },
);

appManager.createSocketListener<{ id: number }, AppState>(
  'blueprint_deleted',
  (state, result) => {
    if (result?.id) {
      delete state.blueprints.items[result.id];
    }
  },
);
